import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useToast } from '../hooks/use-toast';

// Matches MUI Table cells, MUI DataGrid cells, and ARIA grid cells
const CELL_SELECTOR = 'td, th, .MuiDataGrid-cell, [role="cell"], [role="columnheader"]';
// Right-clicking these keeps the native browser menu (links, inputs, buttons, selection editing)
const INTERACTIVE_SELECTOR = 'a, button, input, textarea, select, [contenteditable="true"]';

const MENU_WIDTH = 220;
const MENU_HEIGHT = 72;

async function copyText(text) {
  if (navigator.clipboard && window.isSecureContext) {
    await navigator.clipboard.writeText(text);
    return;
  }
  // Fallback for non-secure contexts
  const textarea = document.createElement('textarea');
  textarea.value = text;
  textarea.style.position = 'fixed';
  textarea.style.opacity = '0';
  document.body.appendChild(textarea);
  textarea.select();
  document.execCommand('copy');
  document.body.removeChild(textarea);
}

/**
 * Global right-click-to-copy for table cells.
 * Mounted once at the app root; intercepts contextmenu events on any table cell
 * and offers a small floating menu to copy the cell's text content.
 */
function TableCellCopyMenu() {
  const [menu, setMenu] = useState(null); // { x, y, text }
  const menuRef = useRef(null);
  const { toast } = useToast();

  const closeMenu = useCallback(() => setMenu(null), []);

  useEffect(() => {
    const handleContextMenu = (event) => {
      const cell = event.target.closest(CELL_SELECTOR);
      if (!cell) {
        return;
      }
      if (event.target.closest(INTERACTIVE_SELECTOR)) {
        return;
      }
      const text = (cell.innerText || '').trim();
      if (!text) {
        return;
      }

      event.preventDefault();

      // Keep the menu inside the viewport
      const x = Math.min(event.clientX, window.innerWidth - MENU_WIDTH - 8);
      const y = Math.min(event.clientY, window.innerHeight - MENU_HEIGHT - 8);
      setMenu({ x, y, text });
    };

    document.addEventListener('contextmenu', handleContextMenu);
    return () => document.removeEventListener('contextmenu', handleContextMenu);
  }, []);

  useEffect(() => {
    if (!menu) {
      return;
    }

    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        closeMenu();
      }
    };
    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        closeMenu();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);
    window.addEventListener('scroll', closeMenu, true);
    window.addEventListener('resize', closeMenu);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
      window.removeEventListener('scroll', closeMenu, true);
      window.removeEventListener('resize', closeMenu);
    };
  }, [menu, closeMenu]);

  const handleCopy = async () => {
    try {
      await copyText(menu.text);
      toast({ title: '已复制', description: menu.text.length > 50 ? `${menu.text.slice(0, 50)}…` : menu.text });
    } catch (err) {
      toast({ title: '复制失败', description: '请手动选择文本复制', variant: 'destructive' });
    }
    closeMenu();
  };

  if (!menu) {
    return null;
  }

  return (
    <div
      ref={menuRef}
      className="fixed z-[9999] min-w-[160px] rounded-md border bg-background py-1 shadow-lg"
      style={{ left: menu.x, top: menu.y }}
      onContextMenu={(e) => e.preventDefault()}
    >
      <button
        type="button"
        className="flex w-full items-center px-3 py-1.5 text-left text-sm hover:bg-accent hover:text-accent-foreground"
        onClick={handleCopy}
        autoFocus
      >
        复制
      </button>
      <div className="max-w-[220px] truncate border-t px-3 py-1 text-xs text-muted-foreground">
        {menu.text}
      </div>
    </div>
  );
}

export default TableCellCopyMenu;
